import { useHistory } from "react-router-dom";
import { useCallback } from "react";
import { useRefValue } from "@shared/utils/hooks/misc.ts";

const useHistoryWithQuery = () => {
  const historyRef = useRefValue(useHistory());

  const getQuery = () => {
    return new URLSearchParams(location.search);
  };

  const appendQuery = (
    queryToAppend?: string | Record<string, string>,
    queryKeysToDelete?: string[],
    replace: boolean = true,
    nextLocation?: string
  ) => {
    const updatedPath = getUpdatedPath(
      queryToAppend,
      queryKeysToDelete,
      nextLocation
    );
    replace
      ? historyRef.current.replace(updatedPath)
      : historyRef.current.push(updatedPath);
  };

  const getUpdatedPath = useCallback(
    (
      queryToAppend?: string | Record<string, string>,
      queryKeysToDelete?: string[],
      nextLocation?: string
    ) => {
      const updatedQuery = new URLSearchParams(getQuery().toString());
      const parsedQueryToAppend =
        typeof queryToAppend === "string"
          ? Object.fromEntries(new URLSearchParams(queryToAppend))
          : queryToAppend;
      for (const [key, value] of Object.entries(parsedQueryToAppend || {})) {
        updatedQuery.set(key, value);
      }
      queryKeysToDelete?.forEach((queryKey) => updatedQuery.delete(queryKey));
      return (
        (nextLocation ?? location.pathname) + `?${updatedQuery.toString()}`
      );
    },
    []
  );

  const historyPush = useCallback((path: string) => {
    historyRef.current.push(path + `?${getQuery().toString()}`);
  }, []);

  const historyReplace = useCallback((path: string) => {
    historyRef.current.replace(path + `?${getQuery().toString()}`);
  }, []);

  return {
    appendQuery,
    historyPush,
    historyReplace,
    getUpdatedPath,
  };
};

export default useHistoryWithQuery;
